import { useEffect, useState } from "react";
import AdminLayout from "@/components/AdminLayout";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { getAuditLogs, getUserBehaviorSummary, exportAuditLogs, getUsers } from "@/lib/api";
import { Download, Loader2, User, Clock, Eye, MessageSquare, BarChart3, RefreshCw } from "lucide-react";
import { toast } from "sonner";

export default function AdminAuditLog() {
  const [logs, setLogs] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userSummary, setUserSummary] = useState(null);
  const [summaryDialogOpen, setSummaryDialogOpen] = useState(false);
  const [filters, setFilters] = useState({
    user_id: "",
    action: "",
    start_date: "",
    end_date: ""
  });

  const fetchData = async () => {
    setLoading(true);
    try {
      const [logsRes, usersRes] = await Promise.all([
        getAuditLogs(filters),
        getUsers()
      ]);
      setLogs(logsRes.data || []);
      setUsers(usersRes.data || []);
    } catch (error) {
      toast.error("Failed to load audit logs");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleFilter = () => {
    fetchData();
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const response = await exportAuditLogs(filters);
      
      // Create download link
      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `audit_log_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success("Export downloaded successfully");
    } catch (error) {
      toast.error("Failed to export audit logs");
    } finally {
      setExporting(false);
    }
  };

  const handleViewUserSummary = async (userId) => {
    try {
      const response = await getUserBehaviorSummary(userId);
      setUserSummary(response.data);
      setSummaryDialogOpen(true);
    } catch (error) {
      toast.error("Failed to load user summary");
    }
  };

  const getActionIcon = (action) => {
    switch (action) {
      case "page_visit": return <Eye className="h-4 w-4 text-blue-500" />;
      case "problem_click": return <Eye className="h-4 w-4 text-green-500" />;
      case "ai_question": return <MessageSquare className="h-4 w-4 text-purple-500" />;
      case "session_start": return <Clock className="h-4 w-4 text-orange-500" />;
      default: return <BarChart3 className="h-4 w-4 text-gray-500" />;
    }
  };

  const getActionLabel = (action) => {
    const labels = {
      page_visit: "Page Visit",
      problem_click: "Problem Click",
      ai_question: "AI Question",
      session_start: "Session Start",
      session_end: "Session End",
      vertical_access: "Industry Access"
    };
    return labels[action] || action;
  };

  const getRatingColor = (rating) => {
    switch (rating) {
      case "Very High": return "bg-green-100 text-green-800";
      case "High": return "bg-blue-100 text-blue-800";
      case "Medium": return "bg-yellow-100 text-yellow-800";
      default: return "bg-gray-100 text-gray-800";
    }
  };

  return (
    <AdminLayout title="Audit Log">
      <div className="space-y-6" data-testid="admin-audit-log">
        <div className="flex items-center justify-between">
          <p className="text-slate-600">Track user activity and export behavioral reports</p>
          <Button 
            onClick={handleExport}
            disabled={exporting}
            className="bg-green-600 hover:bg-green-700"
            data-testid="export-audit-btn"
          >
            {exporting ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Download className="h-4 w-4 mr-2" />
            )}
            Export Excel
          </Button>
        </div>

        {/* Filters */}
        <Card className="border-slate-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">Filters</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
              <div className="space-y-2">
                <Label className="text-xs">User</Label>
                <Select 
                  value={filters.user_id} 
                  onValueChange={(v) => setFilters({ ...filters, user_id: v === "all" ? "" : v })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="All users" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Users</SelectItem>
                    {users.map((u) => (
                      <SelectItem key={u.id} value={u.id}>{u.email}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-xs">Action Type</Label>
                <Select 
                  value={filters.action} 
                  onValueChange={(v) => setFilters({ ...filters, action: v === "all" ? "" : v })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="All actions" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Actions</SelectItem>
                    <SelectItem value="page_visit">Page Visit</SelectItem>
                    <SelectItem value="problem_click">Problem Click</SelectItem>
                    <SelectItem value="ai_question">AI Question</SelectItem>
                    <SelectItem value="session_start">Session Start</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-xs">Start Date</Label>
                <Input
                  type="date"
                  value={filters.start_date}
                  onChange={(e) => setFilters({ ...filters, start_date: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label className="text-xs">End Date</Label>
                <Input
                  type="date"
                  value={filters.end_date}
                  onChange={(e) => setFilters({ ...filters, end_date: e.target.value })}
                />
              </div>
              <div className="flex items-end">
                <Button onClick={handleFilter} variant="outline" className="w-full">
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Apply
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Stats Summary */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="border-slate-200">
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-blue-100">
                  <Eye className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{logs.filter(l => l.action === "page_visit").length}</p>
                  <p className="text-xs text-slate-500">Page Visits</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-slate-200">
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-green-100">
                  <BarChart3 className="h-5 w-5 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{logs.filter(l => l.action === "problem_click").length}</p>
                  <p className="text-xs text-slate-500">Problems Viewed</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-slate-200">
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-purple-100">
                  <MessageSquare className="h-5 w-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{logs.filter(l => l.action === "ai_question").length}</p>
                  <p className="text-xs text-slate-500">AI Questions</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-slate-200">
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-orange-100">
                  <User className="h-5 w-5 text-orange-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{new Set(logs.map(l => l.user_id)).size}</p>
                  <p className="text-xs text-slate-500">Unique Users</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Logs Table */}
        <Card className="border-slate-200">
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Timestamp</TableHead>
                  <TableHead>User</TableHead>
                  <TableHead>Action</TableHead>
                  <TableHead>Details</TableHead>
                  <TableHead className="w-24">Summary</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-8">
                      <Loader2 className="h-6 w-6 animate-spin mx-auto text-slate-400" />
                    </TableCell>
                  </TableRow>
                ) : logs.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-8 text-slate-500">
                      No audit logs found
                    </TableCell>
                  </TableRow>
                ) : (
                  logs.slice(0, 100).map((log) => (
                    <TableRow key={log.id}>
                      <TableCell className="text-sm text-slate-600">
                        {new Date(log.timestamp).toLocaleString()}
                      </TableCell>
                      <TableCell className="font-medium text-sm">
                        {log.user_email}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          {getActionIcon(log.action)}
                          <span className="text-sm">{getActionLabel(log.action)}</span>
                        </div>
                      </TableCell>
                      <TableCell className="text-sm text-slate-500 max-w-xs truncate">
                        {JSON.stringify(log.details)}
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleViewUserSummary(log.user_id)}
                          className="text-blue-600 hover:text-blue-800"
                        >
                          View
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* User Summary Dialog */}
        <Dialog open={summaryDialogOpen} onOpenChange={setSummaryDialogOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>User Behavior Summary</DialogTitle>
            </DialogHeader>
            {userSummary && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-slate-200 flex items-center justify-center">
                      <User className="h-5 w-5 text-slate-600" />
                    </div>
                    <div>
                      <p className="font-medium">{userSummary.user_email}</p>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getRatingColor(userSummary.engagement_rating)}`}>
                        {userSummary.engagement_rating} Engagement
                      </span>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="p-3 bg-slate-50 rounded-lg">
                    <p className="text-2xl font-bold text-slate-900">{userSummary.total_sessions}</p>
                    <p className="text-xs text-slate-500">Sessions</p>
                  </div>
                  <div className="p-3 bg-slate-50 rounded-lg">
                    <p className="text-2xl font-bold text-slate-900">
                      {Math.floor(userSummary.total_time_seconds / 60)}m
                    </p>
                    <p className="text-xs text-slate-500">Total Time</p>
                  </div>
                  <div className="p-3 bg-slate-50 rounded-lg">
                    <p className="text-2xl font-bold text-slate-900">{userSummary.pages_visited}</p>
                    <p className="text-xs text-slate-500">Pages Visited</p>
                  </div>
                  <div className="p-3 bg-slate-50 rounded-lg">
                    <p className="text-2xl font-bold text-slate-900">{userSummary.problems_viewed}</p>
                    <p className="text-xs text-slate-500">Problems Viewed</p>
                  </div>
                  <div className="p-3 bg-slate-50 rounded-lg">
                    <p className="text-2xl font-bold text-slate-900">{userSummary.ai_questions_asked}</p>
                    <p className="text-xs text-slate-500">AI Questions</p>
                  </div>
                  <div className="p-3 bg-slate-50 rounded-lg">
                    <p className="text-2xl font-bold text-slate-900">{userSummary.industries_accessed.length}</p>
                    <p className="text-xs text-slate-500">Industries</p>
                  </div>
                </div>

                {userSummary.industries_accessed.length > 0 && (
                  <div>
                    <p className="text-sm font-medium text-slate-700 mb-2">Industries Explored</p>
                    <div className="flex flex-wrap gap-2">
                      {userSummary.industries_accessed.map((ind, idx) => (
                        <span key={idx} className="px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs">
                          {ind}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                <div className="p-4 bg-slate-100 rounded-lg">
                  <p className="text-sm font-medium text-slate-700 mb-1">Behavior Summary</p>
                  <p className="text-sm text-slate-600">{userSummary.behavior_summary}</p>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
}
